/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';

type CircularAudioSpectrumProps = {
  stream: MediaStream | null;
  size?: number; // canvas size in css pixels
  barCount?: number; // number of radial sample points
  innerRadius?: number; // radius where waves start
  color?: string; // fallback stroke color
  lineWidth?: number; // crisp stroke thickness
  gradientColors?: string[]; // optional conic/radial gradient colors
  glow?: boolean; // draw soft glow behind
  rotationSpeed?: number; // radians/sec rotation for subtle motion
  smoothing?: number; // analyser.smoothingTimeConstant 0..1
  fftSize?: number; // analyser.fftSize (power of 2)
  intensity?: number; // multiplier for bar height
  // Ripple mode
  rippleWaves?: boolean; // draw expanding circular waves instead of radial bars
  waveSpeed?: number; // px per second
  waveThickness?: number; // stroke thickness for waves
  emitThreshold?: number; // min RMS amplitude to emit new ripples (0..1)
  softEdges?: boolean; // render ripples with feathered edges (no sharp outline)
  edgeFadePx?: number; // start fading before canvas edge to avoid visible cutoff
  className?: string;
  style?: React.CSSProperties;
};

export default function CircularAudioSpectrum(props: CircularAudioSpectrumProps) {
  const { stream, size = 220, barCount = 120, innerRadius = 95, color = '#8B5CF6', lineWidth = 2, gradientColors = ['#7C3AED', '#3B82F6', '#06B6D4'], glow = true, rotationSpeed = 0, smoothing = 0.75, fftSize = 512, intensity = 1.6, rippleWaves = true, waveSpeed = 90, waveThickness = 2.5, emitThreshold = 0.008, softEdges = true, edgeFadePx = 12, className, style } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number | null>(null);
  const ampSmoothRef = useRef<number>(0);
  const ripplesRef = useRef<Array<{ bornAt: number; radius: number; maxRadius: number }>>([]);
  const lastSpawnRef = useRef<number>(0);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const timeArrayRef = useRef<Uint8Array | null>(null);

  // Visual configuration lives in a ref so we don't have to recreate AudioContext when visuals change
  const configRef = useRef({
    barCount,
    innerRadius,
    color,
    lineWidth,
    gradientColors,
    glow,
    rotationSpeed,
    intensity,
    rippleWaves,
    waveSpeed,
    waveThickness,
    emitThreshold,
    softEdges,
    edgeFadePx,
  });

  // Keep configRef in sync with latest props
  useEffect(() => {
    configRef.current = {
      barCount,
      innerRadius,
      color,
      lineWidth,
      gradientColors,
      glow,
      rotationSpeed,
      intensity,
      rippleWaves,
      waveSpeed,
      waveThickness,
      emitThreshold,
      softEdges,
      edgeFadePx,
    };
  }, [barCount, innerRadius, color, lineWidth, gradientColors, glow, rotationSpeed, intensity, rippleWaves, waveSpeed, waveThickness, emitThreshold, softEdges, edgeFadePx]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    // Backing store size for crisp rendering
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    // Ensure css size stays as requested
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
  }, [size]);

  // Create audio graph and start loop only when stream changes
  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) return;

    let mounted = true;

    const setup = async () => {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') {
          try {
            await audioContext.resume();
          } catch {}
        }

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        const analyser = audioContext.createAnalyser();
        // Initialize with current params
        const sanitizedFft = Math.max(32, Math.min(32768, Math.pow(2, Math.round(Math.log2(fftSize)))));
        analyser.fftSize = sanitizedFft;
        analyser.smoothingTimeConstant = Math.min(0.99, Math.max(0, smoothing));
        analyserRef.current = analyser;
        source.connect(analyser);

        // allocate buffers
        // Use explicit ArrayBuffer to satisfy TS lib expectations (ArrayBuffer vs ArrayBufferLike)
        dataArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
        timeArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize));

        const draw = (now: number) => {
          if (!mounted) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Compute frame delta time (seconds) for time-based animation
          if (lastFrameRef.current == null) lastFrameRef.current = now;
          const dt = Math.min(0.05, Math.max(0.0005, (now - lastFrameRef.current) / 1000));
          lastFrameRef.current = now;

          const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

          const w = canvas.width / dpr;
          const h = canvas.height / dpr;
          ctx.clearRect(0, 0, w, h);

          // Ensure buffers match analyser sizes
          if (!dataArrayRef.current || dataArrayRef.current.length !== analyser.frequencyBinCount) {
            dataArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
          }
          if (!timeArrayRef.current || timeArrayRef.current.length !== analyser.fftSize) {
            timeArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize));
          }
          const dataArray = dataArrayRef.current;
          const timeArray = timeArrayRef.current;

          // TS lib type expects Uint8Array<ArrayBuffer>; our arrays are compatible at runtime
          analyser.getByteFrequencyData(dataArray as any);
          analyser.getByteTimeDomainData(timeArray as any);

          const centerX = w / 2;
          const centerY = h / 2;

          // Keep visuals fully inside the canvas: account for glow, stroke and blur
          const { gradientColors: cfgGradientColors, glow: cfgGlow, innerRadius: cfgInnerRadius, intensity: cfgIntensity, rippleWaves: cfgRippleWaves, waveSpeed: cfgWaveSpeed, waveThickness: cfgWaveThickness, emitThreshold: cfgEmitThreshold, softEdges: cfgSoftEdges, barCount: cfgBarCount, color: cfgColor, lineWidth: cfgLineWidth, rotationSpeed: cfgRotationSpeed, edgeFadePx: cfgEdgeFadePx } = configRef.current;
          const edgeMargin = cfgGlow ? 24 : 10; // reserved space towards edges
          const availableRadius = Math.max(0, Math.min(w, h) / 2 - edgeMargin);
          // Ensure the chosen base fits with additional wave extension
          const desiredBase = cfgInnerRadius;
          const radiusBase = Math.min(desiredBase, Math.max(availableRadius - 60, 0));
          // dynamic extension budget based on remaining space (relative to actual base)
          const dynamicMaxExtra = Math.max(18, availableRadius - radiusBase - 12);
          const maxExtra = Math.max(16, dynamicMaxExtra) * Math.max(0.5, cfgIntensity);

          // Audio amplitude (RMS of time-domain signal)
          let sum = 0;
          for (let i = 0; i < timeArray.length; i++) {
            const v = (timeArray[i] - 128) / 128; // -1..1
            sum += v * v;
          }
          const rms = Math.sqrt(sum / timeArray.length); // 0..~1
          // Smooth amplitude for visual stability
          const prevAmp = ampSmoothRef.current;
          const amp = prevAmp * 0.8 + rms * 0.2;
          ampSmoothRef.current = amp;

          // Subtle rotation/pulse over time
          if (startTimeRef.current == null) startTimeRef.current = now;
          const elapsed = (now - startTimeRef.current) / 1000;
          const rotation = cfgRotationSpeed * elapsed;
          const pulse = 1 + Math.sin(elapsed * 2) * 0.03; // gentle breathing

          ctx.save();
          ctx.translate(centerX, centerY);
          if (cfgRotationSpeed !== 0) ctx.rotate(rotation);
          ctx.translate(-centerX, -centerY);

          // Gradient (prefer conic if available)
          let gradient: CanvasGradient | null = null;
          const anyCtx: any = ctx as any;
          if (typeof anyCtx.createConicGradient === 'function') {
            gradient = anyCtx.createConicGradient(0, centerX, centerY);
            const stops = cfgGradientColors.length;
            cfgGradientColors.forEach((c, i) => gradient!.addColorStop(i / stops, c));
            gradient!.addColorStop(1, cfgGradientColors[0]);
          } else {
            gradient = ctx.createRadialGradient(centerX, centerY, radiusBase, centerX, centerY, radiusBase + maxExtra + 40);
            const stops = cfgGradientColors.length;
            cfgGradientColors.forEach((c, i) => gradient!.addColorStop(i / (stops - 1 || 1), c));
          }

          if (cfgRippleWaves) {
            // Spawn ripples based on amplitude (higher amp → more frequent and farther)
            const nowMs = now;
            const baseInterval = 260; // ms
            const minInterval = 80; // ms
            const interval = Math.max(minInterval, baseInterval - amp * 180);
            if (amp > cfgEmitThreshold && nowMs - lastSpawnRef.current > interval) {
              lastSpawnRef.current = nowMs;
              const reach = radiusBase + (0.25 + amp * 0.75) * maxExtra; // how far this ripple can travel
              ripplesRef.current.push({ bornAt: nowMs, radius: radiusBase, maxRadius: Math.min(reach, availableRadius) });
              // Limit number of ripples stored
              if (ripplesRef.current.length > 48) ripplesRef.current.shift();
            }

            // Update and draw ripples
            ctx.strokeStyle = gradient || cfgColor;
            ctx.lineCap = 'round';
            const rings = ripplesRef.current;
            for (let i = 0; i < rings.length; i++) {
              const r = rings[i];
              r.radius = Math.min(r.maxRadius, r.radius + cfgWaveSpeed * (1 + amp * 0.5) * dt);
              const effectiveMax = Math.max(radiusBase + 1, r.maxRadius - cfgEdgeFadePx);
              const progress = (r.radius - radiusBase) / Math.max(1, effectiveMax - radiusBase);
              const alpha = Math.max(0, 1 - progress);
              if (r.radius >= r.maxRadius - 0.5 || alpha <= 0.02) {
                // retire
                rings.splice(i, 1);
                i--;
                continue;
              }
              // Optional subtle glow
              if (cfgGlow) {
                ctx.save();
                ctx.globalCompositeOperation = 'lighter';
                ctx.globalAlpha = 0.18 * alpha;
                ctx.lineWidth = Math.max(6, cfgWaveThickness * 8);
                (ctx as any).filter = 'blur(10px)';
                ctx.beginPath();
                ctx.arc(centerX, centerY, r.radius, 0, Math.PI * 2);
                ctx.stroke();
                (ctx as any).filter = 'none';
                ctx.restore();
              }
              // Main ring (soft or crisp)
              ctx.save();
              ctx.globalAlpha = 0.65 * alpha;
              if (cfgSoftEdges) {
                ctx.globalCompositeOperation = 'lighter';
                (ctx as any).filter = 'blur(4px)';
                ctx.lineWidth = Math.max(2, cfgWaveThickness * 2.2);
              } else {
                ctx.lineWidth = Math.max(1.5, cfgWaveThickness);
              }
              ctx.beginPath();
              ctx.arc(centerX, centerY, r.radius, 0, Math.PI * 2);
              ctx.stroke();
              (ctx as any).filter = 'none';
              ctx.restore();
            }
          } else {
            // Fallback: classic smooth radial shape
            const points = cfgBarCount;
            const values: number[] = [];
            for (let i = 0; i < points; i++) {
              const t = i / points;
              const idx = Math.min(analyser.frequencyBinCount - 1, Math.floor(Math.pow(t, 0.7) * analyser.frequencyBinCount));
              const v = Math.min(1.5, (dataArray[idx] / 255) * Math.max(0.5, cfgIntensity));
              values.push(v);
            }
            const path = new Path2D();
            for (let i = 0; i <= points; i++) {
              const idx = i % points;
              const angle = (idx / points) * Math.PI * 2;
              const envelope = 0.6 + 0.4 * Math.sin(idx * 0.5);
              const rad = radiusBase + pulse * (values[idx] * maxExtra * envelope + 16 * envelope);
              const x = centerX + Math.cos(angle) * rad;
              const y = centerY + Math.sin(angle) * rad;
              if (i === 0) path.moveTo(x, y);
              else path.lineTo(x, y);
            }
            path.closePath();

            if (cfgGlow) {
              ctx.save();
              ctx.strokeStyle = gradient || cfgColor;
              ctx.lineWidth = Math.max(6, cfgLineWidth * 6);
              ctx.globalAlpha = 0.25;
              (ctx as any).filter = 'blur(10px)';
              ctx.lineJoin = 'round';
              ctx.lineCap = 'round';
              ctx.stroke(path);
              (ctx as any).filter = 'none';
              ctx.restore();
            }
            ctx.strokeStyle = gradient || cfgColor;
            ctx.lineWidth = Math.max(2, cfgLineWidth * 2);
            ctx.globalAlpha = 0.9;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.stroke(path);
          }

          // Inner ring (avatar border accent)
          ctx.beginPath();
          ctx.arc(centerX, centerY, Math.max(6, radiusBase - 6), 0, Math.PI * 2);
          ctx.strokeStyle = gradient || cfgColor;
          ctx.lineWidth = 3;
          ctx.globalAlpha = 0.8;
          ctx.stroke();

          ctx.restore();

          rafRef.current = requestAnimationFrame(draw);
        };

        rafRef.current = requestAnimationFrame(draw);
      } catch (e) {
        // optional visualizer; ignore failures
      }
    };

    setup();

    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        sourceRef.current?.disconnect();
        analyserRef.current?.disconnect();
      } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
      const ctx = audioContextRef.current;
      if (ctx) {
        try {
          ctx.close();
        } catch {}
        audioContextRef.current = null;
      }
      startTimeRef.current = null;
    };
  }, [stream]);

  // Update analyser parameters without recreating context
  useEffect(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const sanitizedFft = Math.max(32, Math.min(32768, Math.pow(2, Math.round(Math.log2(fftSize)))));
    if (analyser.fftSize !== sanitizedFft) {
      analyser.fftSize = sanitizedFft;
      dataArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
      timeArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    }
    analyser.smoothingTimeConstant = Math.min(0.99, Math.max(0, smoothing));
  }, [fftSize, smoothing]);

  return <canvas ref={canvasRef} className={className} style={style} />;
}
